import React, { useState, useEffect } from 'react';
import { Truck, Wrench, Settings, MapPin, Eye, Calendar, DollarSign, Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';

const Equipment = () => {
  const { user } = useAuth();
  const [ownedEquipment, setOwnedEquipment] = useState([]);
  const [rentalEquipment, setRentalEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Owned Equipment States
  const [ownedFilterCategory, setOwnedFilterCategory] = useState('all');
  const [ownedFilterFuelType, setOwnedFilterFuelType] = useState('all');
  const [ownedViewMode, setOwnedViewMode] = useState('list'); // Default to list view
  
  // Rental Equipment States
  const [showAddForm, setShowAddForm] = useState(false);
  const [showRentForm, setShowRentForm] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [rentalFilterStatus, setRentalFilterStatus] = useState('all');
  const [rentalFilterCategory, setRentalFilterCategory] = useState('all');
  const [rentalFilterVendor, setRentalFilterVendor] = useState('all');
  const [rentalFilterFuelType, setRentalFilterFuelType] = useState('all');

  const [newEquipment, setNewEquipment] = useState({
    equipment_name: '',
    equipment_type: '',
    equipment_category: '',
    vendor: '',
    fuel_type: '',
    model: '',
    rental_rate: '',
    rental_period: 'daily',
    capacity: '',
    weight: '',
    dimensions: '',
    status: 'available'
  });

  const [rentalData, setRentalData] = useState({
    project_id: '',
    start_date: '',
    end_date: '',
    rental_rate: '',
    notes: ''
  });

  useEffect(() => {
    fetchAllEquipment();
    fetchProjects();
  }, []);

  const fetchAllEquipment = async () => {
    setIsLoading(true);
    try {
      // Fetch owned equipment
      const ownedResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owned-equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (ownedResponse.ok) {
        const ownedData = await ownedResponse.json();
        setOwnedEquipment(ownedData);
      }

      // Fetch rental equipment
      const rentalResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rental-equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (rentalResponse.ok) {
        const rentalData = await rentalResponse.json();
        setRentalEquipment(rentalData);
      }
      
    } catch (error) {
      console.error('Error fetching equipment:', error);
      toast.error('Failed to load equipment');
    } finally {
      setIsLoading(false);
    }
  };

  const markOilChange = async (item) => {
    const suggestion = item.last_odometer_km != null ? String(item.last_odometer_km) : '';
    const km = window.prompt(`Odometer (km) when ${item.equipment_name}'s oil was changed:`, suggestion);
    if (km === null) return;
    const clean = km.replace(/[^0-9]/g, '');
    if (!clean) { toast.error('Enter the km reading the oil was changed at'); return; }
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/equipment/${item.id}/oil-change`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ km: Number(clean) })
      });
      if (!r.ok) throw new Error();
      toast.success(`${item.equipment_name}: oil change recorded at ${Number(clean).toLocaleString()} km`);
      fetchAllEquipment();
    } catch { toast.error('Failed to record the oil change'); }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Owned Equipment Functions
  const filteredOwnedEquipment = ownedEquipment.filter(item => {
    const matchesCategory = ownedFilterCategory === 'all' || item.equipment_category === ownedFilterCategory;
    const matchesFuelType = ownedFilterFuelType === 'all' || item.fuel_type === ownedFilterFuelType;
    return matchesCategory && matchesFuelType;
  });

  const getStatusColor = (status) => {
    const colors = {
      'operational': 'bg-green-50 text-green-700 border-green-200',
      'maintenance': 'bg-yellow-50 text-amber-700 border-yellow-200',
      'repair': 'bg-red-50 text-red-600 border-red-200',
      'retired': 'bg-slate-200 text-slate-500 border-slate-300',
      'available': 'bg-green-50 text-green-700 border-green-200',
      'rented': 'bg-blue-50 text-blue-700 border-blue-200',
      'maintenance_required': 'bg-yellow-50 text-amber-700 border-yellow-200'
    };
    return colors[status] || 'bg-slate-200 text-slate-500 border-slate-300';
  };

  // Rental Equipment Functions
  const filteredRentalEquipment = rentalEquipment.filter(item => {
    const matchesStatus = rentalFilterStatus === 'all' || item.status === rentalFilterStatus;
    const matchesCategory = rentalFilterCategory === 'all' || item.equipment_category === rentalFilterCategory;
    const matchesVendor = rentalFilterVendor === 'all' || item.vendor === rentalFilterVendor;
    const matchesFuelType = rentalFilterFuelType === 'all' || item.fuel_type === rentalFilterFuelType;
    return matchesStatus && matchesCategory && matchesVendor && matchesFuelType;
  });

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rental-equipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newEquipment)
      });

      if (response.ok) {
        toast.success('Equipment added successfully');
        setShowAddForm(false);
        setNewEquipment({
          equipment_name: '',
          equipment_type: '',
          equipment_category: '',
          vendor: '',
          fuel_type: '',
          model: '',
          rental_rate: '',
          rental_period: 'daily',
          capacity: '',
          weight: '',
          dimensions: '',
          status: 'available'
        });
        fetchAllEquipment();
      } else {
        toast.error('Failed to add equipment');
      }
    } catch (error) {
      console.error('Error adding equipment:', error);
      toast.error('Error adding equipment');
    }
  };

  const handleRentEquipment = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rental-equipment/${selectedEquipment.id}/rent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(rentalData)
      });

      if (response.ok) {
        toast.success('Equipment rented successfully');
        setShowRentForm(false);
        setRentalData({
          project_id: '',
          start_date: '',
          end_date: '',
          rental_rate: '',
          notes: ''
        });
        fetchAllEquipment();
      } else {
        toast.error('Failed to rent equipment');
      }
    } catch (error) {
      console.error('Error renting equipment:', error);
      toast.error('Error renting equipment');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-slate-900">Loading equipment...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Truck className="h-8 w-8 text-blue-700" />
          <h1 className="text-3xl font-bold">Equipment Management</h1>
        </div>

        <Tabs defaultValue="owned" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-900/10 backdrop-blur-sm">
            <TabsTrigger value="owned" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">
              <Wrench className="h-4 w-4 mr-2" />
              Owned Equipment ({ownedEquipment.length})
            </TabsTrigger>
            <TabsTrigger value="rental" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">
              <Calendar className="h-4 w-4 mr-2" />
              Rental Equipment ({rentalEquipment.length})
            </TabsTrigger>
          </TabsList>

          {/* Owned Equipment Tab */}
          <TabsContent value="owned" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Company Owned Equipment</h2>
              <div className="flex gap-4">
                {/* View Toggle */}
                <div className="flex bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOwnedViewMode('list')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      ownedViewMode === 'list' 
                        ? 'bg-blue-600 text-white' 
                        : 'text-slate-700 hover:bg-white/50'
                    }`}
                  >
                    List View
                  </button>
                  <button
                    onClick={() => setOwnedViewMode('grid')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      ownedViewMode === 'grid' 
                        ? 'bg-blue-600 text-white' 
                        : 'text-slate-700 hover:bg-white/50'
                    }`}
                  >
                    Grid View
                  </button>
                </div>
                <select
                  value={ownedFilterCategory}
                  onChange={(e) => setOwnedFilterCategory(e.target.value)}
                  className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-900"
                >
                  <option value="all">All Categories</option>
                  <option value="crane">Crane</option>
                  <option value="forklift">Forklift</option>
                  <option value="scissor_lift">Scissor Lift</option>
                  <option value="truck">Truck</option>
                  <option value="trailer">Trailer</option>
                  <option value="generator">Generator</option>
                  <option value="welder">Welder</option>
                  <option value="other">Other</option>
                </select>
                <select
                  value={ownedFilterFuelType}
                  onChange={(e) => setOwnedFilterFuelType(e.target.value)}
                  className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-900"
                >
                  <option value="all">All Fuel Types</option>
                  <option value="diesel">Diesel</option>
                  <option value="electric">Electric</option>
                  <option value="gasoline">Gasoline</option>
                  <option value="propane">Propane</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="n/a">N/A</option>
                </select>
              </div>
            </div>

            {/* List View */}
            {ownedViewMode === 'list' && (
              <div className="bg-white/60 backdrop-blur-xl rounded-xl overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Make/Model</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VIN / Serial #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fuel Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inspection / CVOR</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOwnedEquipment.map(item => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.equipment_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.make ? `${item.make} ${item.model || ''}` : 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.vin || item.serial_number || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.equipment_category}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.fuel_type || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={getStatusColor(item.status)}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.annual_inspection || item.cvor_expiry_date || item.expiry_date || 'Not Set'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Grid View */}
            {ownedViewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredOwnedEquipment.map(item => (
                  <Card key={item.id} className="bg-white/60 backdrop-blur-xl border-slate-200 hover:bg-white/70 transition-all">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg text-slate-900">{item.equipment_name}</CardTitle>
                        <Badge className={getStatusColor(item.status)}>
                          {item.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-600">ID:</span>
                          <div className="text-slate-900 font-medium">{item.id}</div>
                        </div>
                        <div>
                          <span className="text-slate-600">Category:</span>
                          <div className="text-slate-900">{item.equipment_category}</div>
                        </div>
                        <div>
                          <span className="text-slate-600">Make/Model:</span>
                          <div className="text-slate-900">{item.make ? `${item.make} ${item.model || ''}` : 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-slate-600">Serial #:</span>
                          <div className="text-slate-900">{item.serial_number || 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-slate-600">Fuel Type:</span>
                          <div className="text-slate-900">{item.fuel_type || 'N/A'}</div>
                        </div>
                        <div>
                          <span className="text-slate-600">Inspection:</span>
                          <div className="text-slate-900">{item.annual_inspection || 'Not Set'}</div>
                        </div>
                      </div>
                      
                      {(item.equipment_type || item.equipment_category || '').toLowerCase().includes('truck') && (
                        <div className="flex items-center justify-between gap-2 bg-slate-100/70 rounded-lg px-3 py-2 text-sm" data-testid={`oil-status-${item.id}`}>
                          <div>
                            <span className="text-slate-600">Oil:</span>{' '}
                            <span className="text-slate-900 font-medium">
                              {item.last_oil_change_km != null ? `last @ ${Number(item.last_oil_change_km).toLocaleString()} km` : 'not tracked yet'}
                            </span>
                            {item.last_odometer_km != null && (
                              <span className="text-slate-500"> · now {Number(item.last_odometer_km).toLocaleString()} km</span>
                            )}
                            {item.last_oil_change_km != null && item.last_odometer_km != null &&
                              (item.last_odometer_km - item.last_oil_change_km) >= 10000 && (
                              <span className="ml-1 text-red-600 font-bold">DUE</span>
                            )}
                          </div>
                          <Button variant="outline" size="sm" data-testid={`oil-change-btn-${item.id}`}
                            onClick={() => markOilChange(item)}
                            className="bg-white/80 border-slate-200 text-slate-900 hover:bg-white whitespace-nowrap">
                            Oil changed
                          </Button>
                        </div>
                      )}

                      {item.location && (
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <MapPin className="h-4 w-4" />
                          {item.location}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="bg-white/80 border-slate-200 text-slate-900 hover:bg-white">
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {filteredOwnedEquipment.length === 0 && (
              <div className="text-center py-12">
                <Truck className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-500 mb-2">No owned equipment found</h3>
                <p className="text-gray-500">No equipment matches your current filters.</p>
              </div>
            )}
          </TabsContent>

          {/* Rental Equipment Tab */}
          <TabsContent value="rental" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Rental Equipment</h2>
              <div className="flex gap-4">
                <select
                  value={rentalFilterStatus}
                  onChange={(e) => setRentalFilterStatus(e.target.value)}
                  className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-900"
                >
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="rented">Rented</option>
                  <option value="maintenance_required">Maintenance Required</option>
                </select>
                
                {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'dispatch') && (
                  <Button
                    onClick={() => setShowAddForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Equipment
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRentalEquipment.map(item => (
                <Card key={item.id} className="bg-white border-slate-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-slate-900">{item.equipment_name}</CardTitle>
                      <Badge className={getStatusColor(item.status)}>
                        {item.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Vendor:</span>
                        <div className="text-slate-900">{item.vendor}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Category:</span>
                        <div className="text-slate-900">{item.equipment_category}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Rate:</span>
                        <div className="text-slate-900">${item.rental_rate}/{item.rental_period}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Capacity:</span>
                        <div className="text-slate-900">{item.capacity || 'N/A'}</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-slate-100 border-slate-300 text-slate-900 hover:bg-slate-200"
                        onClick={() => {
                          setSelectedEquipment(item);
                          setShowRentForm(true);
                        }}
                        disabled={item.status === 'rented'}
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        {item.status === 'rented' ? 'Rented' : 'Rent'}
                      </Button>
                      <Button variant="outline" size="sm" className="bg-slate-100 border-slate-300 text-slate-900 hover:bg-slate-200">
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredRentalEquipment.length === 0 && (
              <div className="text-center py-12">
                <Truck className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-500 mb-2">No rental equipment found</h3>
                <p className="text-gray-500">No equipment matches your current filters.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Equipment Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Add New Rental Equipment</h3>
              <form onSubmit={handleAddEquipment} className="space-y-4">
                <div>
                  <Label className="text-slate-700">Equipment Name</Label>
                  <Input
                    value={newEquipment.equipment_name}
                    onChange={(e) => setNewEquipment({...newEquipment, equipment_name: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700">Vendor</Label>
                  <Input
                    value={newEquipment.vendor}
                    onChange={(e) => setNewEquipment({...newEquipment, vendor: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700">Rental Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newEquipment.rental_rate}
                    onChange={(e) => setNewEquipment({...newEquipment, rental_rate: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                    Add Equipment
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                    className="bg-slate-100 border-slate-300 text-slate-900 hover:bg-slate-200"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Rent Equipment Modal */}
        {showRentForm && selectedEquipment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                Rent {selectedEquipment.equipment_name}
              </h3>
              <form onSubmit={handleRentEquipment} className="space-y-4">
                <div>
                  <Label className="text-slate-700">Project</Label>
                  <select
                    value={rentalData.project_id}
                    onChange={(e) => setRentalData({...rentalData, project_id: e.target.value})}
                    className="w-full bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.project_number} - {project.client_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-700">Start Date</Label>
                  <Input
                    type="date"
                    value={rentalData.start_date}
                    onChange={(e) => setRentalData({...rentalData, start_date: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700">End Date</Label>
                  <Input
                    type="date"
                    value={rentalData.end_date}
                    onChange={(e) => setRentalData({...rentalData, end_date: e.target.value})}
                    className="bg-slate-100 border-slate-300 text-slate-900"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                    Rent Equipment
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowRentForm(false)}
                    className="bg-slate-100 border-slate-300 text-slate-900 hover:bg-slate-200"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Equipment;